import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Placeholder from '@tiptap/extension-placeholder';
import { useEffect } from 'react';
import { useTranslation } from '../../../i18n/useTranslation';
import './RichTextEditor.css';

interface RichTextEditorProps {
  value: string;
  onChange: (html: string) => void;
  placeholder?: string;
  disabled?: boolean;
  hasError?: boolean;
  'aria-describedby'?: string;
  'aria-labelledby'?: string;
}

interface ToolbarButtonProps {
  onClick: () => void;
  isActive?: boolean;
  disabled?: boolean;
  title: string;
  children: React.ReactNode;
}

function ToolbarButton({ onClick, isActive, disabled, title, children }: ToolbarButtonProps) {
  return (
    <button
      type="button"
      title={title}
      aria-label={title}
      aria-pressed={isActive}
      disabled={disabled}
      className={`rte-toolbar__btn${isActive ? ' rte-toolbar__btn--active' : ''}`}
      onMouseDown={(e) => {
        e.preventDefault(); // keep editor focused
        onClick();
      }}
    >
      {children}
    </button>
  );
}

export function RichTextEditor({
  value,
  onChange,
  placeholder,
  disabled = false,
  hasError = false,
  'aria-describedby': ariaDescribedBy,
  'aria-labelledby': ariaLabelledBy,
}: RichTextEditorProps) {
  const { t } = useTranslation();
  const editor = useEditor({
    extensions: [
      StarterKit,
      Placeholder.configure({ placeholder: placeholder ?? '' }),
    ],
    content: value,
    editable: !disabled,
    editorProps: ariaLabelledBy
      ? { attributes: { 'aria-labelledby': ariaLabelledBy } }
      : undefined,
    onUpdate({ editor: ed }) {
      const html = ed.getHTML();
      // Treat an empty paragraph as empty string so backend doesn't store '<p></p>'
      onChange(html === '<p></p>' ? '' : html);
    },
  });

  // Sync external value changes (e.g. when editing an existing entry)
  useEffect(() => {
    if (!editor) return;
    const current = editor.getHTML();
    const normalised = current === '<p></p>' ? '' : current;
    if (normalised !== value) {
      editor.commands.setContent(value || '');
    }
  }, [value, editor]);

  // Sync disabled state
  useEffect(() => {
    if (!editor) return;
    editor.setEditable(!disabled);
  }, [disabled, editor]);

  if (!editor) return null;

  const canHeading = (level: 1 | 2 | 3) => editor.can().toggleHeading({ level });

  return (
    <div
      className={[
        'rte',
        hasError ? 'rte--error' : '',
        disabled ? 'rte--disabled' : '',
      ].filter(Boolean).join(' ')}
      aria-describedby={ariaDescribedBy}
    >
      <div className="rte-toolbar" role="toolbar" aria-label={t('common.richText.toolbar')}>
        {/* Heading buttons */}
        <ToolbarButton
          title={t('common.richText.heading', { level: 1 })}
          isActive={editor.isActive('heading', { level: 1 })}
          disabled={!canHeading(1) || disabled}
          onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
        >
          H1
        </ToolbarButton>
        <ToolbarButton
          title={t('common.richText.heading', { level: 2 })}
          isActive={editor.isActive('heading', { level: 2 })}
          disabled={!canHeading(2) || disabled}
          onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
        >
          H2
        </ToolbarButton>
        <ToolbarButton
          title={t('common.richText.heading', { level: 3 })}
          isActive={editor.isActive('heading', { level: 3 })}
          disabled={!canHeading(3) || disabled}
          onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
        >
          H3
        </ToolbarButton>

        <span className="rte-toolbar__divider" aria-hidden="true" />

        {/* Inline formatting */}
        <ToolbarButton
          title={t('common.richText.boldText')}
          isActive={editor.isActive('bold')}
          disabled={!editor.can().toggleBold() || disabled}
          onClick={() => editor.chain().focus().toggleBold().run()}
        >
          <strong>B</strong>
        </ToolbarButton>
        <ToolbarButton
          title={t('common.richText.italic')}
          isActive={editor.isActive('italic')}
          disabled={!editor.can().toggleItalic() || disabled}
          onClick={() => editor.chain().focus().toggleItalic().run()}
        >
          <em>I</em>
        </ToolbarButton>
        <ToolbarButton
          title={t('common.richText.strikethrough')}
          isActive={editor.isActive('strike')}
          disabled={!editor.can().toggleStrike() || disabled}
          onClick={() => editor.chain().focus().toggleStrike().run()}
        >
          <s>S</s>
        </ToolbarButton>

        <span className="rte-toolbar__divider" aria-hidden="true" />

        {/* Lists */}
        <ToolbarButton
          title={t('common.richText.bulletList')}
          isActive={editor.isActive('bulletList')}
          disabled={disabled}
          onClick={() => editor.chain().focus().toggleBulletList().run()}
        >
          ≡
        </ToolbarButton>
        <ToolbarButton
          title={t('common.richText.orderedList')}
          isActive={editor.isActive('orderedList')}
          disabled={disabled}
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
        >
          №
        </ToolbarButton>

        <span className="rte-toolbar__divider" aria-hidden="true" />

        {/* Blockquote */}
        <ToolbarButton
          title={t('common.richText.blockquote')}
          isActive={editor.isActive('blockquote')}
          disabled={disabled}
          onClick={() => editor.chain().focus().toggleBlockquote().run()}
        >
          "
        </ToolbarButton>
      </div>

      <EditorContent editor={editor} className="rte-content" />
    </div>
  );
}
